/**
 * /menu — public, read-only menu. Anyone can browse items and prices with no
 * account; to actually order, they sign in (tapping an item routes to /login).
 * Signed-in users are sent to the real orderable menu (/order) or their home,
 * mirroring the Landing redirect — this preview is for logged-out visitors.
 */
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MenuGrid } from '../components/menu/MenuGrid';
import { MenuSkeleton } from '../components/menu/MenuSkeleton';
import { useAuth } from '../hooks/useAuth';
import { useMenu } from '../hooks/useMenu';

export function PublicMenu() {
  const { isAuthed, role, isLoading } = useAuth();
  const navigate = useNavigate();
  const { categories, items, isLoading: menuLoading, error } = useMenu();

  // Signed-in users get the real, orderable experience — not this read-only
  // preview.
  if (isAuthed && !isLoading) {
    return <Navigate to={role === 'barista' ? '/kitchen' : '/order'} replace />;
  }

  return (
    <div className="min-h-dvh bg-wca-chalk">
      <header
        className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-wca-gray-light bg-wca-chalk/95 px-4 backdrop-blur sm:px-6"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top))', paddingBottom: '0.75rem' }}
      >
        <Link to="/" className="font-display text-xl text-wca-black">
          The Cracked Mug<span className="text-wca-gold">.</span>
        </Link>
        <Link
          to="/login"
          className="group inline-flex items-center gap-1.5 rounded-full bg-wca-red px-5 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(196,18,48,0.5)] transition-all hover:bg-wca-red-dark active:scale-[0.98]"
        >
          Sign in to order
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      </header>

      <div className="px-4 pt-6 sm:px-6">
        <h1 className="font-display text-3xl text-wca-black sm:text-4xl">The menu</h1>
        <p className="mt-1.5 text-sm text-wca-gray-mid">
          Browse what we&rsquo;re serving — sign in to build your drink and order.
        </p>
      </div>

      {menuLoading ? (
        <MenuSkeleton />
      ) : error !== null ? (
        <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
          <p className="font-display text-2xl">Couldn&rsquo;t load the menu</p>
          <p className="mt-2 max-w-md text-sm text-wca-gray-mid">{error}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
          <p className="font-display text-2xl">The menu is coming soon</p>
          <p className="mt-2 max-w-md text-sm text-wca-gray-mid">Check back shortly.</p>
        </div>
      ) : (
        <MenuGrid
          categories={categories}
          items={items}
          showFavorites={false}
          onSelectItem={() => navigate('/login')}
        />
      )}
    </div>
  );
}
